import { Feedback, Inline, SelectInput, styled, TextInput } from 'canoe';
import { Field, FieldAttributes, useFormikContext } from 'formik';
import get from 'lodash/get';
import has from 'lodash/has';
import isNumber from 'lodash/isNumber';
import React, { useEffect, useMemo, useState } from 'react';

import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { DefinitionFieldWrapper } from './DefinitionFormComponents';

const InputWrapper = styled(DefinitionFieldWrapper)`
  min-width: 80px;
`;

interface TimeframeInputProps {
  name: string;
  readOnly?: boolean;
}

const timeframes = {
  days: 86400000,
  hours: 3600000,
  minutes: 60000,
  seconds: 1000,
};

type Timeframe = keyof typeof timeframes;

export const TimeframeInput = ({ name, readOnly }: TimeframeInputProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  // always default to the best divisible target
  const [timeframe, setTimeframe] = useState(
    (): Timeframe => {
      const value = get(values, name);
      if (value % timeframes.days === 0) {
        return 'days';
      }
      if (value % timeframes.hours === 0) {
        return 'hours';
      }
      if (value % timeframes.minutes === 0) {
        return 'minutes';
      }
      if (value % timeframes.seconds === 0) {
        return 'seconds';
      }
      return 'days';
    },
  );

  const handleValueChange = (e: any) => {
    setFieldValue(
      name,
      e.target.value !== '' ? e.target.value * timeframes[timeframe] : e.target.value,
      false,
    );
  };

  const displayValue = (value: number | string, type: Timeframe) => {
    if (!isNumber(value)) {
      return '';
    }
    return value / timeframes[type];
  };

  const handleTimeframeChanged = (newTimeframe: any) => {
    setTimeframe(newTimeframe.value);
  };

  const currentValue = useMemo(() => {
    const value = get(values, name);

    // if the input type is numeric and it contains a non-numeric value, set as empty
    if (value !== '' && !isNumber(value)) {
      return '';
    }
    return displayValue(value, timeframe);
  }, [values, name, timeframe]);

  useEffect(() => {
    if (!has(values, name)) {
      setFieldValue(name, '', false);
    } else if (Array.isArray(currentValue)) {
      // if we are switching from multiselect to normal dropdown,
      // convert array to string by keeping the first value only
      setFieldValue(name, currentValue[0], false);
    }
  }, []);

  return (
    <Inline align="center" space="xs">
      <Field name={name}>
        {({ field, meta }: FieldAttributes<any>) => (
          <InputWrapper>
            <TextInput
              {...field}
              onChange={handleValueChange}
              data-cy="segment-definition-input"
              id={`definition-${name}`}
              value={currentValue}
              type="number"
              state={meta.touched && meta.error ? 'danger' : 'default'}
              disabled={readOnly}
            />
            {meta.touched && meta.error && (
              <Feedback mt="xxs" color="danger">
                {meta.error}
              </Feedback>
            )}
          </InputWrapper>
        )}
      </Field>
      <SelectInput
        clearable={false}
        selectedItem={{ value: timeframe, label: timeframe }}
        onSelect={handleTimeframeChanged}
        items={Object.keys(timeframes).map((option) => ({
          value: option,
          label: option,
        }))}
      />
    </Inline>
  );
};
